import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users,
  Building2,
  Shield,
  Lock,
  Activity,
  AlertTriangle,
  UserCheck,
  Globe,
  RefreshCw,
  Download,
  Settings,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import backendApi from '../../services/backendApi';
import {
  downloadOnboardingTemplate,
  getFallbackOnboardingSchema,
  OnboardingSchemaMap,
  OnboardingEntity,
} from '../../utils/adminBulkOnboarding';

type AdminStats = {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  totalSites: number;
  activeSites: number;
  inactiveSites: number;
  totalCompanies: number;
  activeCompanies: number;
  inactiveCompanies: number;
  lockedAccounts: number;
  recentLogins: number;
  newUsersToday: number;
  newUsersThisWeek: number;
  newUsersThisMonth: number;
  failedLoginsToday: number;
  recentLoginAttempts24h: number;
  totalLoginAttempts: number;
  successfulLogins: number;
  failedLogins: number;
};

type AdditionalData = {
  usersBySite: any[];
  roleDistribution: any[];
  loginByHour: any[];
  suspiciousIPs: any[];
  recentLogins: any[];
  context: {
    company_name?: string;
    site_name?: string;
    is_global: boolean;
  } | null;
};

const EMPTY_STATS: AdminStats = {
  totalUsers: 0,
  activeUsers: 0,
  inactiveUsers: 0,
  totalSites: 0,
  activeSites: 0,
  inactiveSites: 0,
  totalCompanies: 0,
  activeCompanies: 0,
  inactiveCompanies: 0,
  lockedAccounts: 0,
  recentLogins: 0,
  newUsersToday: 0,
  newUsersThisWeek: 0,
  newUsersThisMonth: 0,
  failedLoginsToday: 0,
  recentLoginAttempts24h: 0,
  totalLoginAttempts: 0,
  successfulLogins: 0,
  failedLogins: 0,
};

const EMPTY_ADDITIONAL_DATA: AdditionalData = {
  usersBySite: [],
  roleDistribution: [],
  loginByHour: [],
  suspiciousIPs: [],
  recentLogins: [],
  context: null,
};

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const roleName = String(user?.roleName || user?.role || '').toLowerCase();
  const isSystemAdmin = !!user?.isGlobalAdmin || !!(user as any)?.is_superuser || ['system_admin', 'super_admin'].includes(roleName);
  const isHoAdmin = roleName === 'ho_admin' || roleName === 'company_admin' || (roleName === 'admin' && !user?.siteCode);
  const isSiteAdmin = roleName === 'site_admin' || (roleName === 'admin' && !!user?.siteCode);

  const [stats, setStats] = useState<AdminStats>(EMPTY_STATS);
  const [additionalData, setAdditionalData] = useState<AdditionalData>(EMPTY_ADDITIONAL_DATA);
  const [loading, setLoading] = useState(true);
  const [isRealTime, setIsRealTime] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [onboardingSchema, setOnboardingSchema] = useState<Partial<OnboardingSchemaMap>>(getFallbackOnboardingSchema());
  const [downloadingEntity, setDownloadingEntity] = useState<string | null>(null);

  const handleDownloadTemplate = async (entity: OnboardingEntity) => {
    setDownloadingEntity(entity);
    try {
      await downloadOnboardingTemplate(entity, onboardingSchema);
    } catch (error) {
      console.error(`Failed to download ${entity} template:`, error);
      alert(`Failed to download ${entity} template. Please try again.`);
    } finally {
      setDownloadingEntity(null);
    }
  };

  useEffect(() => {
    void fetchAdminStats(true);
  }, []);

  useEffect(() => {
    const loadOnboardingSchema = async () => {
      const response = await backendApi.getAdminOnboardingSchema();
      if (response.success && response.schema) {
        setOnboardingSchema(response.schema);
      }
    };

    void loadOnboardingSchema();
  }, []);

  useEffect(() => {
    if (!isRealTime) {
      return;
    }

    let timeoutId: NodeJS.Timeout | null = null;
    let isMounted = true;
    let currentInterval = 10000;
    let consecutiveErrors = 0;

    const handlePolling = async () => {
      if (!isMounted) return;

      if (document.visibilityState === 'visible') {
        try {
          const token = sessionStorage.getItem('access_token');
          if (!token) {
            consecutiveErrors++;
            currentInterval = Math.min(10000 * Math.pow(2, consecutiveErrors), 60000);
            if (isMounted) timeoutId = setTimeout(handlePolling, currentInterval);
            return;
          }

          await fetchAdminStats(false);
          consecutiveErrors = 0;
          currentInterval = 10000;
        } catch (error: any) {
          consecutiveErrors++;
          currentInterval = Math.min(10000 * Math.pow(2, consecutiveErrors), 60000);
        }
      } else {
        // if not visible, wait and try again
        currentInterval = 10000;
      }

      if (isMounted) {
        timeoutId = setTimeout(handlePolling, currentInterval);
      }
    };

    timeoutId = setTimeout(() => {
      if (isMounted) handlePolling();
    }, currentInterval);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (timeoutId) clearTimeout(timeoutId);
        handlePolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isRealTime]);

  const fetchAdminStats = async (showLoading = false) => {
    try {
      if (showLoading) {
        setLoading(true);
      } else {
        setIsUpdating(true);
      }

      const activeSiteId = isSystemAdmin
        ? null
        : Number(sessionStorage.getItem('site_id') || localStorage.getItem('site_id') || 0) || null;
      const activeSiteCode = isSystemAdmin
        ? null
        : sessionStorage.getItem('tenant') || localStorage.getItem('currentSite') || null;
      const activeCompanyId = isSystemAdmin
        ? null
        : Number(sessionStorage.getItem('company_id') || localStorage.getItem('company_id') || 0) || null;

      const response = await backendApi.getAdminDashboardStats(activeCompanyId, activeSiteId, activeSiteCode);
      if (!response.success) {
        return;
      }

      const usersData = response.data?.users || response.users || {};
      const sitesData = response.data?.sites || response.sites || {};
      const companiesData = response.data?.companies || response.companies || {};
      const securityData = response.data?.security || response.security || {};

      setStats({
        totalUsers: usersData?.total || 0,
        activeUsers: usersData?.active || 0,
        inactiveUsers: usersData?.inactive || 0,
        totalSites: sitesData?.total || 0,
        activeSites: sitesData?.active || 0,
        inactiveSites: sitesData?.inactive || 0,
        totalCompanies: companiesData?.total || 0,
        activeCompanies: companiesData?.active || 0,
        inactiveCompanies: companiesData?.inactive || 0,
        lockedAccounts: securityData?.locked_accounts || 0,
        recentLogins: Array.isArray(securityData?.recent_logins) ? securityData.recent_logins.length : 0,
        newUsersToday: usersData?.new_today || 0,
        newUsersThisWeek: usersData?.new_this_week || 0,
        newUsersThisMonth: usersData?.new_this_month || 0,
        failedLoginsToday: securityData?.failed_today || 0,
        recentLoginAttempts24h: securityData?.recent_attempts_24h || 0,
        totalLoginAttempts: securityData?.total_login_attempts || 0,
        successfulLogins: securityData?.successful_logins || 0,
        failedLogins: securityData?.failed_logins || 0,
      });

      setAdditionalData({
        usersBySite: usersData?.by_site || [],
        roleDistribution: usersData?.role_distribution || [],
        loginByHour: securityData?.login_by_hour || [],
        suspiciousIPs: securityData?.suspicious_ips || [],
        recentLogins: Array.isArray(securityData?.recent_logins) ? securityData.recent_logins : [],
        context: response.data?.context || response.context || null,
      });
    } catch (error) {
      console.error('Error fetching admin stats:', error);
    } finally {
      setIsUpdating(false);
      setLoading(false);
    }
  };

  const adminCards = useMemo(() => {
    const allCards = [
      {
        title: 'User Management',
        description: 'Manage users, roles, and permissions.',
        icon: <Users size={22} />,
        action: () => navigate('/admin/users'),
        stat: stats.totalUsers,
        statLabel: 'Total users',
        roles: ['system_admin', 'ho_admin', 'company_admin', 'site_admin'],
      },
      {
        title: 'Site Management',
        description: 'Review site operations, status, and assignments.',
        icon: <Building2 size={22} />,
        action: () => navigate('/admin/sites'),
        stat: stats.totalSites,
        statLabel: 'Total sites',
        roles: ['system_admin', 'ho_admin', 'company_admin'],
      },
      {
        title: 'Customer Management',
        description: 'Control customer entities, plans, and provisioning state.',
        icon: <Building2 size={22} />,
        action: () => navigate('/admin/companies'),
        stat: stats.totalCompanies,
        statLabel: 'Total customers',
        roles: ['system_admin'],
      },
      {
        title: 'Security & Monitoring',
        description: 'Inspect login behavior, lockouts, and security signals.',
        icon: <Shield size={22} />,
        action: () => navigate('/admin/security'),
        stat: stats.lockedAccounts,
        statLabel: 'Locked accounts',
        roles: ['system_admin', 'ho_admin', 'company_admin'],
      },
    ];

    const effectiveRole = isSystemAdmin ? 'system_admin' : isHoAdmin ? 'ho_admin' : isSiteAdmin ? 'site_admin' : 'general_user';
    return allCards.filter((card) => card.roles.includes(effectiveRole));
  }, [stats, navigate, isSystemAdmin, isHoAdmin, isSiteAdmin]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center admin-shell">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0084CF]/30 border-t-[#0084CF] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] py-3 sm:py-5 lg:py-6 px-2 sm:px-3 lg:px-4">
      <div className="max-w-7xl mx-auto space-y-6">
        <motion.section
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="admin-hero p-4 sm:p-4 lg:p-5"
        >
          <div className="admin-toolbar">
            <div className="space-y-2">
              <p className="admin-eyebrow">Admin overview</p>
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex w-11 h-11 items-center justify-center rounded-xl bg-white/90 border border-[#E3F0F9]/50 shadow-md shadow-[#0084CF]/5 flex-shrink-0">
                  <img src="/12376087.png" alt="Settings" className="w-7 h-7 object-contain" />
                </div>
                <div>
                  <h1 className="admin-page-title">Control center for people, customers, and sites.</h1>
                  <p className="admin-page-subtitle mt-2">
                    {additionalData.context?.is_global
                      ? 'A live administrative view of the full AxonMetric network with operational, user, and security signals in one place.'
                      : 'A focused administrative view for the active operational context, with cleaner access to user, site, and customer workflows.'}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    <span className="admin-pill admin-pill-info">
                      <Activity size={12} />
                      {isRealTime ? 'Real-time updates enabled' : 'Manual refresh mode'}
                    </span>
                    <span className="admin-pill admin-pill-muted">
                      <Globe size={12} />
                      {additionalData.context?.is_global ? 'Global scope' : additionalData.context?.company_name || 'Selected company'}
                    </span>
                    {additionalData.context?.site_name && (
                      <span className="admin-pill admin-pill-success">
                        <Building2 size={12} />
                        {additionalData.context.site_name}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setIsRealTime((value) => !value)}
                className={`admin-button ${isRealTime ? 'admin-button-secondary' : 'admin-button-ghost'}`}
                title={isRealTime ? 'Disable real-time updates' : 'Enable real-time updates'}
              >
                <Activity size={16} />
                {isRealTime ? 'Live mode' : 'Paused'}
              </button>
              <button onClick={() => fetchAdminStats(true)} className="admin-button admin-button-primary" title="Refresh overview" aria-label="Refresh overview">
                <RefreshCw size={16} className={isUpdating ? 'animate-spin' : ''} />
                Refresh overview
              </button>
            </div>
          </div>

          <div className="admin-stat-strip mt-6">
            <div className="admin-stat-card">
              <p className="admin-stat-label">Users</p>
              <p className="admin-stat-value">{stats.totalUsers}</p>
              <p className="admin-stat-hint">{stats.activeUsers} active across the current scope</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Sites</p>
              <p className="admin-stat-value">{stats.totalSites}</p>
              <p className="admin-stat-hint">{stats.activeSites} online and administratively active</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Customers</p>
              <p className="admin-stat-value">{stats.totalCompanies}</p>
              <p className="admin-stat-hint">{stats.activeCompanies} customer accounts enabled</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Security</p>
              <p className="admin-stat-value">{stats.lockedAccounts}</p>
              <p className="admin-stat-hint">{stats.failedLoginsToday} failed logins recorded today</p>
            </div>
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }} className="admin-section">
          <div className="admin-section-header">
            <div>
              <h2 className="admin-section-title">Onboarding templates</h2>
              <p className="admin-section-copy">Download empty Excel templates for company, site, and user bulk onboarding.</p>
            </div>
          </div>
          <div className="admin-section-body">
            <div className="admin-row-list">
              {isSystemAdmin && (
                <button
                  onClick={() => void handleDownloadTemplate('companies')}
                  disabled={downloadingEntity !== null}
                  className="admin-row text-left hover:translate-y-[-1px] transition-transform disabled:opacity-60 disabled:cursor-not-allowed w-full"
                >
                  <div className="admin-row-leading">
                    <div className="admin-row-icon">
                      {downloadingEntity === 'companies' ? (
                        <RefreshCw size={18} className="animate-spin text-[#0084CF]" />
                      ) : (
                        <Building2 size={18} />
                      )}
                    </div>
                    <div>
                      <p className="admin-row-title">Company onboarding sheet</p>
                      <p className="admin-row-subtitle">Headers aligned with the customer creation form fields.</p>
                    </div>
                  </div>
                  <div className="admin-row-actions">
                    <span className="admin-button admin-button-secondary min-w-[120px] flex items-center justify-center gap-1.5">
                      {downloadingEntity === 'companies' ? (
                        <>
                          <RefreshCw size={14} className="animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Download size={14} />
                          Download
                        </>
                      )}
                    </span>
                  </div>
                </button>
              )}

              {(isSystemAdmin || isHoAdmin) && (
                <button
                  onClick={() => void handleDownloadTemplate('sites')}
                  disabled={downloadingEntity !== null}
                  className="admin-row text-left hover:translate-y-[-1px] transition-transform disabled:opacity-60 disabled:cursor-not-allowed w-full"
                >
                  <div className="admin-row-leading">
                    <div className="admin-row-icon">
                      {downloadingEntity === 'sites' ? (
                        <RefreshCw size={18} className="animate-spin text-[#0084CF]" />
                      ) : (
                        <Building2 size={18} />
                      )}
                    </div>
                    <div>
                      <p className="admin-row-title">Site onboarding sheet</p>
                      <p className="admin-row-subtitle">Headers aligned with the site creation flow including company mapping.</p>
                    </div>
                  </div>
                  <div className="admin-row-actions">
                    <span className="admin-button admin-button-secondary min-w-[120px] flex items-center justify-center gap-1.5">
                      {downloadingEntity === 'sites' ? (
                        <>
                          <RefreshCw size={14} className="animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Download size={14} />
                          Download
                        </>
                      )}
                    </span>
                  </div>
                </button>
              )}

              {(isSystemAdmin || isHoAdmin || isSiteAdmin) && (
                <button
                  onClick={() => void handleDownloadTemplate('users')}
                  disabled={downloadingEntity !== null}
                  className="admin-row text-left hover:translate-y-[-1px] transition-transform disabled:opacity-60 disabled:cursor-not-allowed w-full"
                >
                  <div className="admin-row-leading">
                    <div className="admin-row-icon">
                      {downloadingEntity === 'users' ? (
                        <RefreshCw size={18} className="animate-spin text-[#0084CF]" />
                      ) : (
                        <Users size={18} />
                      )}
                    </div>
                    <div>
                      <p className="admin-row-title">User onboarding sheet</p>
                      <p className="admin-row-subtitle">Headers aligned with the user creation form and role-based mapping fields.</p>
                    </div>
                  </div>
                  <div className="admin-row-actions">
                    <span className="admin-button admin-button-secondary min-w-[120px] flex items-center justify-center gap-1.5">
                      {downloadingEntity === 'users' ? (
                        <>
                          <RefreshCw size={14} className="animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Download size={14} />
                          Download
                        </>
                      )}
                    </span>
                  </div>
                </button>
              )}
            </div>
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="admin-section">
          <div className="admin-section-header">
            <div>
              <h2 className="admin-section-title">Admin sections</h2>
              <p className="admin-section-copy">Every core admin entry point is arranged as a focused horizontal row for faster scanning.</p>
            </div>
          </div>
          <div className="admin-section-body">
            <div className="admin-row-list">
              {adminCards.map((card, index) => (
                <motion.button
                  key={card.title}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.04 }}
                  onClick={card.action}
                  className="admin-row text-left hover:translate-y-[-1px] transition-transform"
                >
                  <div className="admin-row-leading">
                    <div className="admin-row-icon">{card.icon}</div>
                    <div className="min-w-0">
                      <p className="admin-row-title">{card.title}</p>
                      <p className="admin-row-subtitle">{card.description}</p>
                      <div className="admin-row-tags">
                        <span className="admin-tag">{card.statLabel}</span>
                      </div>
                    </div>
                  </div>
                  <div className="admin-row-meta">
                    <div className="admin-meta-block">
                      <p className="admin-meta-label">Current value</p>
                      <p className="admin-meta-value">{card.stat}</p>
                    </div>
                    <div className="admin-meta-block">
                      <p className="admin-meta-label">Action</p>
                      <p className="admin-meta-value text-[#1C4D88] font-semibold">Open section</p>
                    </div>
                  </div>
                  <div className="admin-row-actions">
                    <span className="admin-button admin-button-secondary">Open</span>
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
        </motion.section>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }} className="admin-section">
            <div className="admin-section-header">
              <div>
                <h2 className="admin-section-title">Recent activity</h2>
                <p className="admin-section-copy">Latest authentication attempts and administrative movement.</p>
              </div>
              {isRealTime && <span className="admin-pill admin-pill-success">Live</span>}
            </div>
            <div className="admin-section-body">
              {additionalData.recentLogins.length > 0 ? (
                <div className="admin-row-list max-h-[540px] overflow-y-auto pr-1">
                  {additionalData.recentLogins.map((login, index) => (
                    <div key={`${login.username || login.user_email}-${index}`} className="admin-row">
                      <div className="admin-row-leading">
                        <div className="admin-row-icon">{login.success ? <UserCheck size={18} /> : <AlertTriangle size={18} />}</div>
                        <div className="min-w-0">
                          <p className="admin-row-title truncate">{login.user_email || login.username || 'Unknown user'}</p>
                          <p className="admin-row-subtitle truncate">
                            {login.site ? `${login.site} • ` : ''}
                            {new Date(login.attempted_at).toLocaleString()}
                          </p>
                          <div className="admin-row-tags">
                            {login.company && <span className="admin-tag">{login.company}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="admin-row-meta">
                        <div className="admin-meta-block">
                          <p className="admin-meta-label">Result</p>
                          <p className="admin-meta-value">{login.success ? 'Successful login' : 'Failed attempt'}</p>
                        </div>
                        <div className="admin-meta-block">
                          <p className="admin-meta-label">Signal</p>
                          <p className="admin-meta-value">{login.ip_address || 'IP not recorded'}</p>
                        </div>
                      </div>
                      <div className="admin-row-actions">
                        <span className={`admin-pill ${login.success ? 'admin-pill-success' : 'admin-pill-danger'}`}>
                          {login.success ? 'Success' : 'Failed'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="admin-empty">No recent activity available yet.</div>
              )}
            </div>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="admin-section">
            <div className="admin-section-header">
              <div>
                <h2 className="admin-section-title">Users by site</h2>
                <p className="admin-section-copy">A row view of where users are currently concentrated.</p>
              </div>
            </div>
            <div className="admin-section-body">
              {additionalData.usersBySite.length > 0 ? (
                <div className="admin-row-list max-h-[540px] overflow-y-auto pr-1">
                  {additionalData.usersBySite.map((item, index) => (
                    <div key={`${item.site__site_code || 'unknown'}-${index}`} className="admin-row">
                      <div className="admin-row-leading">
                        <div className="admin-row-icon">
                          <Building2 size={18} />
                        </div>
                        <div className="min-w-0">
                          <p className="admin-row-title truncate">{item.site__site_name || 'Unknown site'}</p>
                          <p className="admin-row-subtitle truncate">{item.site__site_code || 'Site code unavailable'}</p>
                          <div className="admin-row-tags">
                            {item.company_name && <span className="admin-tag">{item.company_name}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="admin-row-meta">
                        <div className="admin-meta-block">
                          <p className="admin-meta-label">Assigned users</p>
                          <p className="admin-meta-value">{item.count || 0}</p>
                        </div>
                        <div className="admin-meta-block">
                          <p className="admin-meta-label">Category</p>
                          <p className="admin-meta-value">Operational distribution</p>
                        </div>
                      </div>
                      <div className="admin-row-actions">
                        <span className="admin-pill admin-pill-info">{item.count || 0} users</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="admin-empty">No site distribution data is available yet.</div>
              )}
            </div>
          </motion.section>
        </div>

        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.26 }} className="admin-section">
          <div className="admin-section-header">
            <div>
              <h2 className="admin-section-title">Quick actions</h2>
              <p className="admin-section-copy">High-frequency administrative tasks presented in the same row-first layout.</p>
            </div>
          </div>
          <div className="admin-section-body">
            <div className="admin-row-list">
              {(isSystemAdmin || isHoAdmin || isSiteAdmin) && (
                <button onClick={() => navigate('/admin/users/create')} className="admin-row text-left hover:translate-y-[-1px] transition-transform">
                  <div className="admin-row-leading">
                    <div className="admin-row-icon"><Users size={18} /></div>
                    <div>
                      <p className="admin-row-title">Create user</p>
                      <p className="admin-row-subtitle">Add a new account and assign company, site, and module access.</p>
                    </div>
                  </div>
                  <div className="admin-row-meta">
                    <div className="admin-meta-block">
                      <p className="admin-meta-label">Workflow</p>
                      <p className="admin-meta-value">Identity and role onboarding</p>
                    </div>
                    <div className="admin-meta-block">
                      <p className="admin-meta-label">Availability</p>
                      <p className="admin-meta-value">Admin roles only</p>
                    </div>
                  </div>
                  <div className="admin-row-actions"><span className="admin-button admin-button-primary">Create</span></div>
                </button>
              )}

              {(isSystemAdmin || isHoAdmin) && (
                <button onClick={() => navigate('/admin/sites/create')} className="admin-row text-left hover:translate-y-[-1px] transition-transform">
                  <div className="admin-row-leading">
                    <div className="admin-row-icon"><Building2 size={18} /></div>
                    <div>
                      <p className="admin-row-title">Create site</p>
                      <p className="admin-row-subtitle">Register a new site with company context and module policy defaults.</p>
                    </div>
                  </div>
                  <div className="admin-row-meta">
                    <div className="admin-meta-block">
                      <p className="admin-meta-label">Workflow</p>
                      <p className="admin-meta-value">Operational rollout</p>
                    </div>
                    <div className="admin-meta-block">
                      <p className="admin-meta-label">Availability</p>
                      <p className="admin-meta-value">System and HO admins</p>
                    </div>
                  </div>
                  <div className="admin-row-actions"><span className="admin-button admin-button-secondary">Create</span></div>
                </button>
              )}

              {(isSystemAdmin || isHoAdmin) && (
                <button onClick={() => navigate('/admin/security')} className="admin-row text-left hover:translate-y-[-1px] transition-transform">
                  <div className="admin-row-leading">
                    <div className="admin-row-icon"><Lock size={18} /></div>
                    <div>
                      <p className="admin-row-title">Review security logs</p>
                      <p className="admin-row-subtitle">Inspect lockouts, suspicious activity, and recent authentication attempts.</p>
                    </div>
                  </div>
                  <div className="admin-row-meta">
                    <div className="admin-meta-block">
                      <p className="admin-meta-label">Locked accounts</p>
                      <p className="admin-meta-value">{stats.lockedAccounts}</p>
                    </div>
                    <div className="admin-meta-block">
                      <p className="admin-meta-label">Failed today</p>
                      <p className="admin-meta-value">{stats.failedLoginsToday}</p>
                    </div>
                  </div>
                  <div className="admin-row-actions"><span className="admin-button admin-button-ghost">Open logs</span></div>
                </button>
              )}
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default AdminDashboard;
